import { useEffect, useRef } from "react";
import type { WpSeo } from "@/lib/seo";

type AlternateLink = {
  href: string;
  hrefLang: string;
};

export type PageHeadProps = {
  title?: string;
  description?: string;
  canonical?: string;
  alternates?: AlternateLink[];
  seo?: WpSeo | null;
};

const env = import.meta.env as Record<string, string | undefined>;
const CANONICAL_BASE = (env.VITE_SITE_URL || env.VITE_CANONICAL_BASE || "https://www.onplexus.com")
  .trim()
  .replace(/\/$/, "");
const DEFAULT_TITLE = "OnPlexus: Global Currency Orchestration Platform";

function toAbsoluteUrl(href?: string | null) {
  if (!href) return null;
  if (/^(https?:)?\/\//i.test(href)) {
    return href;
  }
  const normalized = href.startsWith("/") ? href : `/${href}`;
  return `${CANONICAL_BASE}${normalized}`;
}

function normalizeString(value?: string | null) {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

export function PageHead({ title, description, canonical, alternates, seo }: PageHeadProps) {
  const injectionIdRef = useRef(`onp-seo-${Math.random().toString(36).slice(2)}`);
  const appendedRef = useRef<Element[]>([]);
  const seoHead = normalizeString(seo?.head);

  const resolvedTitle = normalizeString(seo?.title) ?? normalizeString(title) ?? DEFAULT_TITLE;
  const resolvedDescription = normalizeString(seo?.description) ?? normalizeString(description);
  const robotsFromSeo = normalizeString(seo?.robots);
  const resolvedRobots = robotsFromSeo && /noindex/i.test(robotsFromSeo) ? "index,follow" : robotsFromSeo ?? "index,follow";
  const resolvedCanonical = normalizeString(seo?.canonical) ?? normalizeString(canonical);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const previousTitle = document.title;
    document.title = resolvedTitle || DEFAULT_TITLE;

    let descriptionMeta: HTMLMetaElement | null = null;
    let previousDescription: string | null = null;
    let robotsMeta: HTMLMetaElement | null = null;
    let previousRobots: string | null = null;

    if (resolvedDescription) {
      descriptionMeta = document.querySelector("meta[name='description']");
      if (!descriptionMeta) {
        descriptionMeta = document.createElement("meta");
        descriptionMeta.name = "description";
        document.head.appendChild(descriptionMeta);
      }
      previousDescription = descriptionMeta.getAttribute("content");
      descriptionMeta.setAttribute("content", resolvedDescription);
    }

    if (resolvedRobots) {
      robotsMeta = document.querySelector("meta[name='robots']");
      if (!robotsMeta) {
        robotsMeta = document.createElement("meta");
        robotsMeta.name = "robots";
        document.head.appendChild(robotsMeta);
      }
      previousRobots = robotsMeta.getAttribute("content");
      robotsMeta.setAttribute("content", resolvedRobots);
    }

    return () => {
      document.title = previousTitle || DEFAULT_TITLE;
      if (descriptionMeta && resolvedDescription) {
        if (previousDescription !== null) {
          descriptionMeta.setAttribute("content", previousDescription);
        } else {
          descriptionMeta.removeAttribute("content");
        }
      }
      if (robotsMeta && resolvedRobots) {
        if (previousRobots !== null) {
          robotsMeta.setAttribute("content", previousRobots);
        } else {
          robotsMeta.removeAttribute("content");
        }
      }
    };
  }, [resolvedTitle, resolvedDescription, resolvedRobots]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const absCanonical = toAbsoluteUrl(resolvedCanonical);
    let canonicalLink = document.querySelector("link[data-onp-canonical='true']") as HTMLLinkElement | null;

    if (absCanonical) {
      if (!canonicalLink) {
        canonicalLink = document.createElement("link");
        canonicalLink.setAttribute("rel", "canonical");
        canonicalLink.setAttribute("data-onp-canonical", "true");
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute("href", absCanonical);
    } else if (canonicalLink) {
      canonicalLink.parentNode?.removeChild(canonicalLink);
      canonicalLink = null;
    }

    const existingAlternates = Array.from(
      document.querySelectorAll("link[data-onp-hreflang='true']")
    ) as HTMLLinkElement[];
    existingAlternates.forEach((link) => link.parentNode?.removeChild(link));

    const created: HTMLLinkElement[] = [];
    (alternates ?? []).forEach(({ href, hrefLang }) => {
      const absHref = toAbsoluteUrl(href);
      if (!absHref || !hrefLang) return;
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hrefLang", hrefLang);
      link.setAttribute("href", absHref);
      link.setAttribute("data-onp-hreflang", "true");
      document.head.appendChild(link);
      created.push(link);
    });

    return () => {
      created.forEach((link) => link.parentNode?.removeChild(link));
    };
  }, [resolvedCanonical, alternates]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    if (appendedRef.current.length) {
      appendedRef.current.forEach((node) => node.parentNode?.removeChild(node));
      appendedRef.current = [];
    }

    if (!seoHead) {
      return;
    }

    const template = document.createElement("template");
    template.innerHTML = seoHead;
    const nodes = Array.from(template.content.children);
    if (nodes.length === 0) {
      return;
    }

    const appended: Element[] = [];
    const marker = injectionIdRef.current;

    nodes.forEach((node) => {
      const element = node as Element;
      const tag = element.tagName.toLowerCase();

      if (tag === "title") {
        const text = element.textContent?.trim();
        if (text) {
          document.title = text;
        }
        return;
      }

      if (tag === "meta") {
        const name = element.getAttribute("name")?.toLowerCase();
        if (name === "description" || name === "robots") {
          return;
        }
      }

      if (tag === "link") {
        const rel = element.getAttribute("rel")?.toLowerCase();
        if (rel === "canonical" || rel === "alternate") {
          return;
        }
      }

      element.setAttribute("data-onp-seo", marker);
      document.head.appendChild(element);
      appended.push(element);
    });

    appendedRef.current = appended;

    return () => {
      appended.forEach((node) => node.parentNode?.removeChild(node));
      appendedRef.current = [];
    };
  }, [seoHead]);

  return null;
}
